import {
  Button,
  Card,
  Col,
  ConfigProvider,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Table,
  notification,
} from 'antd';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  exportCourierDistanceReport,
  getCourierDistanceReport,
} from '../../../../store/requests/reports';
import 'moment/locale/tr';
import trTR from 'antd/es/locale/tr_TR';
import { useSelector } from 'react-redux';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { useHistory } from 'react-router-dom';
import { PAGE_URLS } from 'helpers/endpoints';
import debounce from 'lodash/debounce';
import moment from 'moment';
import { CSVLink } from 'react-csv';
import {
  COURIER_TYPE,
  CSV_EXPORT_COLUMNS_DISTANCE_REPORT,
  debouncedDataLoadWaitMs,
  EXPORT_LIMIT
} from 'helpers/constants';
import { DownloadOutlined } from '@ant-design/icons';
import DebounceSelect from '../../../../components/DebounceSelect';
import { getCouriers } from '../../../../store/requests/courier';

const { Option } = Select;
let lastQuery = false;

const Index = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [fetching, setFetching] = useState(false);
  const [distanceReport, setDistanceReport] = useState(null);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const userRegions = useSelector((state) => state?.definitions?.userRegions);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [stores, setStores] = useState([]);
  const [payloadData, setPayloadData] = useState(null);
  const [dates, setDates] = useState([]);
  const [downloadedData, setDownloadedData] = useState([]);
  const [exportButtonLoading, setExportButtonLoading] = useState(false);
  const [ascendingEstimateDistance, setAscendingEstimateDistance] =
    useState(null);
  const [ascendingAirDistance, setAscendingAirDistance] = useState(null);
  const userStores = useSelector((state) => state?.definitions?.userStores);
  const [tableParams, setTableParams] = useState({
    pagination: {
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `Toplam ${total} kayıt`,
      pageSizeOptions: [30, 60, 90],
      size: 'small',
      current: 1,
      pageSize: 30,
    },
  });
  const csvLink = useRef();
  const numberOfCols = userRegions.length > 0 ? 6 : 8;

  useEffect(() => {
    if (userRegions.length < 1) {
      setStores(userStores);
    }
  }, [userRegions]);

  useEffect(() => {
    if (payloadData) {
      fetchData();
      return () => {
        lastQuery = null;
        setFetching(false);
      };
    }
  }, [JSON.stringify(tableParams)]);

  const fetchData = () => {
    if (!fetching) {
      setFetching(true);
      const queryString = getQueryString();
      loadData(queryString);
    }
  };

  const getQueryString = () => {
    let queryString;
    queryString = `page=${tableParams.pagination.current - 1}&size=${tableParams.pagination.pageSize}`;

    return queryString;
  };

  const loadData = useCallback(
    debounce((queryString) => {
      loadDataWithDebounce(queryString);
    }, debouncedDataLoadWaitMs),
  );

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter && sorter.columnKey === 'estimatedDistance') {
      const updatedPayloadData = {
        ...payloadData,
        ascendingEstimateDistance: sorter.order
          ? sorter.order === 'ascend'
          : null,
        ascendingAirDistance: null,
      };
      setAscendingEstimateDistance(
        sorter.order ? (sorter.order === 'ascend') : null,
      );
      setAscendingAirDistance(null);
      setPayloadData(updatedPayloadData);
    } else if (sorter && sorter.columnKey === 'airDistance') {
      const updatedPayloadData = {
        ...payloadData,
        ascendingAirDistance: sorter.order
          ? sorter.order === 'ascend'
          : null,
        ascendingEstimateDistance: null,
      };
      setAscendingAirDistance(
        sorter.order ? (sorter.order === 'ascend') : null,
      );
      setAscendingEstimateDistance(null);
      setPayloadData(updatedPayloadData);
    }

    setTableParams({
      pagination,
      ...sorter,
    });
  };

  const handleChangeRegion = async (selected, options) => {
    let allStoresSelectedRegions = [];
    setSelectedRegions([]);
    setSelectedStores([]);

    if (selected.length === 0) {
      setSelectedStores([]);
      setStores([]);
      form.resetFields(['storeIds']);

      return;
    }

    setSelectedRegions(selected);
    selected.forEach((selectedRegion) => {
      const storesOfRegion =
        userRegions.find((region) => region.id === selectedRegion.value)
          ?.children || [];
      allStoresSelectedRegions.push(...storesOfRegion);
    });
    setStores(allStoresSelectedRegions);
  };

  const handleChangeStore = (selected) => {
    setSelectedStores(selected);
  };

  async function loadDataWithDebounce(queryString) {
    if (!fetching) {
      lastQuery = queryString;
      setFetching(true);
      getData(queryString, payloadData);
    }
  }

  const onFinish = (data) => {
    const url = 'page=0&size=30';
    const{ dateRange, ...values} = data;

    const [startDate, endDate] = dates;
    const regionIds = values.regionIds?.map((region) => region.value) || [];
    const courierIds = values.courierIds?.map((item) => item.value) || [];

    const payload = {
      ...values,
      ascendingEstimateDistance,
      ascendingAirDistance,
      startDate: startDate,
      endDate: endDate,
      regionIds,
      courierIds,
    };

    setPayloadData(payload);
    setFetching(true);
    getData(url, payload);
  };

  const getData = (url, payload) => {
    getCourierDistanceReport(url, payload)
      .then((res) => {
        if (res) {
          const { content, page } = res;
          setTableParams({
            ...tableParams,
            pagination: {
              ...tableParams.pagination,
              total: page?.totalElements,
            },
          });
          setDistanceReport(content ?? []);
        }
      })
      .finally(() => {
        setFetching(false);
      });
  };

  const onFinishFailed = () => {
    console.error('Error');
  };

  const columns = [
    {
      title: t('courierName'),
      dataIndex: 'courierFullname',
      key: 'courierFullname',
      sorter: (a, b) => a.courierFullname.localeCompare(b.courierFullname),
      showSorterTooltip: false,
    },
    {
      title: t('platform'),
      dataIndex: 'partner',
      key: 'partner',
      sorter: (a, b) => a.partner.localeCompare(b.partner),
      showSorterTooltip: false,
    },
    {
      title: t('orderNo'),
      dataIndex: 'customerOrderNumber',
      key: 'customerOrderNumber',
      sorter: (a, b) =>
        a.customerOrderNumber.localeCompare(b.customerOrderNumber),
      showSorterTooltip: false,
    },
    {
      title: t('orderDateOfDelivery'),
      dataIndex: 'deliveryDate',
      key: 'deliveryDate',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('storeCode'),
      dataIndex: 'storeCode',
      key: 'storeCode',
      sorter: (a, b) => a.storeCode.localeCompare(b.storeCode),
      showSorterTooltip: false,
    },
    {
      title: t('storeName'),
      dataIndex: 'storeName',
      key: 'storeName',
      sorter: (a, b) => a.storeName.localeCompare(b.storeName),
      showSorterTooltip: false,
    },
    {
      title: t('storeLocation'),
      dataIndex: 'storeLocation',
      key: 'storeLocation',
      sorter: (a, b) => a.storeLocation - b.storeLocation,
      showSorterTooltip: false,
      render: (text, record) =>
        `${record.storeLatitude ? record.storeLatitude?.toFixed(8) : '--'}, ${record.storeLongitude ? record.storeLongitude?.toFixed(8) : '--'}`,
    },
    {
      title: t('addressLocation'),
      dataIndex: 'addressLocation',
      key: 'addressLocation',
      sorter: (a, b) => a.addressLocation - b.addressLocation,
      showSorterTooltip: false,
      render: (text, record) =>
        `${record.addressLatitude ? record.addressLatitude?.toFixed(8) : '--'}, ${record.addressLongitude ? record.addressLongitude?.toFixed(8) : '--'}`,
    },
    {
      title: t('courierType'),
      dataIndex: 'courierType',
      key: 'courierType',
      sorter: (a, b) => a.courierType.localeCompare(b.courierType),
      showSorterTooltip: false,
      render: (text) => t(text),
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => a.status.localeCompare(b.status),
      showSorterTooltip: false,
      render: (text) => t(text),
    },
    {
      title: t('estimatedDistance'),
      dataIndex: 'estimatedDistance',
      key: 'estimatedDistance',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('airDistance'),
      dataIndex: 'airDistance',
      key: 'airDistance',
      sorter: true,
      showSorterTooltip: false,
    },
  ];

  const fetchCSVData = async () => {
    setExportButtonLoading(true);

    try {
      const courierDistanceReportRes =
        await exportCourierDistanceReport(payloadData);
      const distanceReportData = courierDistanceReportRes?.data;

      if (
        distanceReportData &&
        distanceReportData?.length > 0 &&
        distanceReportData?.length <= EXPORT_LIMIT
      ) {
        const csvData = [
          CSV_EXPORT_COLUMNS_DISTANCE_REPORT.map((item) => t(item)),
          ...distanceReportData.map((item) => [
            item.courierFullname,
            item.partner,
            item.customerOrderNumber,
            item.deliveryDate,
            item.storeCode,
            item.storeName,
            item.storeLatitude ? Number(item.storeLatitude).toFixed(8) : '--',
            item.storeLongitude ? Number(item.storeLongitude).toFixed(8) : '--',
            item.addressLatitude
              ? Number(item.addressLatitude).toFixed(8)
              : '--',
            item.addressLongitude
              ? Number(item.addressLongitude).toFixed(8)
              : '--',
            t(item.courierType),
            t(item.status),
            item.estimatedDistance,
            item.airDistance,
          ]),
        ];

        setDownloadedData(csvData);
        csvLink.current.link.click();
      } else {
        if (courierDistanceReportRes?.message) {
          notification.info({ message: courierDistanceReportRes.message });
        }
      }
    } finally {
      setExportButtonLoading(false);
    }
  };

  const datepickerChangeHandler = (dates, datesString) => {
    const [startDate, endDate] = datesString;
    setDates(datesString);
    startDate && endDate ? setButtonDisabled(false) : setButtonDisabled(true);
  };

  const getCouriersByWords = (value) => {
    return getCouriers(`filter=fullName=ilike='${value}'&size=200`).then((res) =>
      res?.length > 0
        ? res?.map((courier) => ({
          label: courier.fullName,
          value: courier.id,
        }))
        : [],
    );
  };

  return (
    <>
      <Card
        actions={[
          <Button
            disabled={buttonDisabled}
            loading={fetching}
            onClick={() => form.submit()}
            type="primary"
          >
            {t('list')}
          </Button>,
        ]}
      >
          <Form
            layout="vertical"
            form={form}
            name="basic"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
          >
            <Row gutter={24}>
              <Col span={numberOfCols}>
                <Form.Item
                  name="dateRange"
                  width="100%"
                  label={t('date')}
                  labelAlign="left"
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || value.length !== 2) {
                          return Promise.resolve();
                        }
                        const startDate = value[0];
                        const endDate = value[1];
                        const maxAllowedDate = moment(startDate).add(
                          32,
                          'days',
                        );

                        if (endDate && endDate.isAfter(maxAllowedDate)) {
                          return Promise.reject({
                            message: t('maxSelectedDateRange'),
                          });
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                  wrapperCol={{ span: 24 }}
                >
                  <DatePicker.RangePicker
                    placeholder={[t('validityStartDate'), t('validityEndDate')]}
                    style={{ width: '100%' }}
                    format={'YYYY-MM-DD'}
                    allowClear={false}
                    onChange={datepickerChangeHandler}
                  />
                </Form.Item>
              </Col>
              {userRegions.length > 0 && (
                <Col span={numberOfCols}>
                  <Form.Item
                    name="regionIds"
                    width="100%"
                    label={t('regions')}
                    labelAlign="left"
                    wrapperCol={{ span: 24 }}
                  >
                    <Select
                      className="fullWidth"
                      mode="multiple"
                      showSearch
                      placeholder={t('selectRegion')}
                      onChange={handleChangeRegion}
                      maxTagCount={1}
                      style={{ width: '100%' }}
                      filterOption={(input, option) =>
                        option.children &&
                        option.children
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      optionFilterProp="value"
                      value={selectedRegions}
                      labelInValue
                    >
                      {userRegions &&
                        userRegions.map((region) => (
                          <Option key={region.id} value={region.id}>
                            {region.title}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
              )}
              <Col span={numberOfCols}>
                <Form.Item
                  name="storeIds"
                  width="100%"
                  label={t('stores')}
                  labelAlign="left"
                  wrapperCol={{ span: 24 }}
                >
                  <Select
                    className="fullWidth"
                    showSearch
                    filterOption={true}
                    optionFilterProp="children"
                    placeholder={t('selectStore')}
                    onChange={handleChangeStore}
                    style={{ width: '100%' }}
                    mode="multiple"
                    maxTagCount={1}
                    value={selectedStores}
                    disabled={
                      userRegions.length > 0 && selectedRegions.length <= 0
                    }
                  >
                    {stores &&
                      stores.map((store) => (
                        <Option key={store.id} value={store.id}>
                          {store.title || store.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={numberOfCols}>
                <Form.Item
                  name="courierIds"
                  label={t('courierName')}
                  labelAlign="left"
                  labelWrap
                  style={{ width: '100%' }}
                >
                  <DebounceSelect
                    mode="multiple"
                    showSearch
                    allowClear
                    placeholder={t('courierName')}
                    fetchOptions={getCouriersByWords}
                    maxTagCount={2}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col span={6}>
                <Form.Item
                  name="partners"
                  width="100%"
                  label={t('platform')}
                  labelAlign="left"
                  wrapperCol={{ span: 24 }}
                >
                  <Select
                    showSearch
                    placeholder={t('select')}
                    style={{ width: '100%' }}
                    filterOption={(input, option) =>
                      option.value &&
                      option.value.toLowerCase().includes(input.toLowerCase())
                    }
                    optionFilterProp="value"
                    allowClear={true}
                    mode="multiple"
                  >
                    <Option key={'SOK'} value={'SOK'}>
                      {'ŞOK'}
                    </Option>
                    <Option key={'ISTEGELSIN'} value={'ISTEGELSIN'}>
                      {'İsteGelsin'}
                    </Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="customerOrderNumber"
                  width="100%"
                  label={t('orderNo')}
                  labelAlign="left"
                  wrapperCol={{ span: 24 }}
                >
                  <Input allowClear placeholder={t('orderNo')} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="orderStatus"
                  width="100%"
                  label={t('orderStatus')}
                  labelAlign="left"
                  wrapperCol={{ span: 24 }}
                >
                  <Select
                    showSearch
                    placeholder={t('select')}
                    style={{ width: '100%' }}
                    filterOption={(input, option) =>
                      option.value &&
                      option.value.toLowerCase().includes(input.toLowerCase())
                    }
                    optionFilterProp="value"
                    allowClear={true}
                  >
                    <Option key={'DELIVERED'} value={'DELIVERED'}>
                      {'Teslim Edildi'}
                    </Option>
                    <Option key={'NOT_DELIVERED'} value={'NOT_DELIVERED'}>
                      {'Teslim Edilemedi'}
                    </Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="courierType"
                  width="100%"
                  label={t('courierType')}
                  labelAlign="left"
                  wrapperCol={{ span: 24 }}
                >
                  <Select
                    showSearch
                    placeholder={t('select')}
                    style={{ width: '100%' }}
                    filterOption={(input, option) =>
                      option.value &&
                      option.value.toLowerCase().includes(input.toLowerCase())
                    }
                    optionFilterProp="value"
                    allowClear={true}
                  >
                    {Object.keys(COURIER_TYPE).map((type) => (
                      <Option key={type} value={type}>
                        {t(type)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Form>
      </Card>
      {distanceReport && (
        <>
          <div style={{ margin: '5px' }}>
            <Button
              style={{ marginRight: '3px' }}
              shape="round"
              icon={<DownloadOutlined />}
              onClick={fetchCSVData}
            ></Button>
            <CSVLink
              filename={'CourierDistanceReport'}
              data={downloadedData}
              ref={csvLink}
              className="hidden"
            />
          </div>
          <ConfigProvider locale={trTR}>
            <Table
              loading={fetching}
              size="small"
              style={{ width: '100%' }}
              columns={columns}
              dataSource={distanceReport.map((item, index) => ({
                ...item,
                key: index.toString(),
              }))}
              pagination={tableParams.pagination}
              onChange={handleTableChange}
              scroll={{
                y: '60vh',
              }}
            />
          </ConfigProvider>
        </>
      )}
    </>
  );
};

export default Index;
